import { useEffect, useRef, useState } from "react";
import CopyButton from "../components/CopyButton";
import { ClaudeCodeScopeTable, ToolPermissionChoices } from "../components/mcp/McpShared";
import { ToolPermissionLabel } from "../components/mcp/ToolPermissionIcon";
import RecentUse from "../components/mcp/RecentUse";
import ShieldIcon from "../icons/ShieldIcon";
import UserIcon from "../icons/UserIcon";
import LinkIcon from "../icons/LinkIcon";
import {
  MCP_BUG_URL,
  MCP_SLACK_LABEL,
  MCP_SLACK_URL,
  connectorName,
  formatAddedDate,
} from "../lib/mcp-links";
import type { McpServer } from "../lib/mcp-registry";
import { onSpaLinkClick } from "../lib/link-click";
import { routeToHash, type McpHelpSectionId } from "../lib/routing";
import { useMcpServers } from "../lib/use-mcp-servers";
import styles from "./McpServerDetailPage.module.css";

interface Props {
  serverName: string;
  onBackToServers: () => void;
  /** Opens the MCP Help topic, optionally on one section. */
  onHelp: (section?: McpHelpSectionId) => void;
  /**
   * My Connections is on (REACT_APP_MY_CONNECTIONS_ENABLED): shows "Your recent use" in the sidebar
   * and links the access note to the page.
   */
  myConnections?: boolean;
  /** Opens My Connections. */
  onOpenConnections?: () => void;
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
      {children}
    </a>
  );
}

function Breadcrumb({ current, onBack }: { current: string; onBack: () => void }) {
  return (
    <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
      <a href={routeToHash({ page: "mcp" })} className={styles.crumbLink} onClick={onSpaLinkClick(onBack)}>
        MCP Servers
      </a>
      <span className={styles.breadcrumbSep} aria-hidden="true">›</span>
      <strong className={styles.breadcrumbCurrent}>{current}</strong>
    </nav>
  );
}

/** The primary action: copies the endpoint, with a pointer to the Server URL box if the clipboard is unavailable. */
function CopyServerUrlButton({ url }: { url: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const flash = (next: "copied" | "failed") => {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), next === "failed" ? 5000 : 2000);
  };
  const handleCopy = () => {
    // navigator.clipboard is undefined outside secure contexts, and writeText rejects when blocked.
    if (!navigator.clipboard) return flash("failed");
    navigator.clipboard.writeText(url).then(() => flash("copied")).catch(() => flash("failed"));
  };

  return (
    <div className={styles.copyAction}>
      <button type="button" onClick={handleCopy} className={styles.copyUrlButton}>
        <LinkIcon width="16" height="16" />
        {state === "copied" ? "Copied!" : "Copy server URL"}
      </button>
      <span role="status" className={state === "failed" ? styles.copyFailed : styles.visuallyHidden}>
        {state === "failed" && "Couldn't copy. Select the Endpoint above and copy it."}
        {state === "copied" && "Server URL copied"}
      </span>
    </div>
  );
}

/** A collapsed how-to under the Copy button, shown as a small link like the prototype's. */
function HowTo({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <details className={styles.howTo}>
      <summary className={styles.howToSummary}>{title}</summary>
      <div className={styles.howToBody}>{children}</div>
    </details>
  );
}

function CopyRow({ label, text }: { label: string; text: string }) {
  return (
    <div className={styles.copyRow}>
      <div className={styles.copyLabel}>{label}</div>
      <CopyButton text={text} />
    </div>
  );
}

function ServerDetail({
  server,
  onHelp,
  myConnections,
  onOpenConnections,
}: {
  server: McpServer;
  onHelp: Props["onHelp"];
  myConnections: boolean;
  onOpenConnections?: () => void;
}) {
  const name = connectorName(server.title);
  const cliCommand = `claude mcp add --transport http ${server.serverName} ${server.url}`;
  const mcpJsonEntry = `"${server.serverName}": { "type": "http", "url": "${server.url}" }`;
  const added = formatAddedDate(server.publishedAt);
  const helpLink = (section: McpHelpSectionId | undefined, label: React.ReactNode, className = styles.link) => (
    <a
      href={section ? routeToHash({ page: "help", section }) : routeToHash({ page: "help", topic: "mcp" })}
      onClick={onSpaLinkClick(() => onHelp(section))}
      className={className}
    >
      {label}
    </a>
  );

  return (
    <div className={styles.layout}>
      <div className={styles.mainCol}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{server.title}</h1>
          <div className={styles.serverName}>{server.serverName}</div>
        </div>

        {server.description && <p className={styles.description}>{server.description}</p>}

        <dl className={styles.facts}>
          <dt>Server name</dt>
          <dd className={styles.mono}>{server.serverName}</dd>
          <dt>Endpoint</dt>
          <dd className={`${styles.mono} ${styles.endpoint}`}>{server.url}</dd>
          {server.version && (
            <>
              <dt>Version</dt>
              <dd>{server.version}</dd>
            </>
          )}
          {added && (
            <>
              <dt>Added</dt>
              <dd>{added}</dd>
            </>
          )}
        </dl>

        {myConnections && (
          <div className={`${styles.note} ${styles.noteNeutral}`}>
            <LinkIcon className={styles.noteIconMuted} />
            <span>
              <strong>First use:</strong> approve the vendor&rsquo;s consent screen once. The gateway keeps the token
              refreshed. Manage or revoke it later in{" "}
              <a
                href={routeToHash({ page: "connections" })}
                onClick={onSpaLinkClick(onOpenConnections)}
                className={styles.link}
              >
                My Connections
              </a>
              .
            </span>
          </div>
        )}

        <div className={styles.note}>
          <UserIcon className={styles.noteIconBrand} />
          <span>
            <strong>Open to any Cox employee</strong> who signs in with Entra. What you can see and do depends on your own
            account in {name}. Credentials are always per person: you use your own access, and the platform never holds a
            shared credential for you.
            {myConnections && (
              <>
                {" "}
                <a
                  href={routeToHash({ page: "connections" })}
                  onClick={onSpaLinkClick(onOpenConnections)}
                  className={styles.link}
                >
                  See what the platform holds for you in My Connections →
                </a>
              </>
            )}
          </span>
        </div>

        <div className={`${styles.note} ${styles.noteNeutral}`}>
          <ShieldIcon className={styles.noteIconMuted} />
          <span>
            <strong>Choose when Claude can use each tool.</strong> After you connect in Claude Desktop, open the connector to
            set each tool, or a whole group such as Read-only tools, to{" "}
            <ToolPermissionLabel kind="allow" className={styles.perm} />,{" "}
            <ToolPermissionLabel kind="ask" className={styles.perm} /> or{" "}
            <ToolPermissionLabel kind="block" className={styles.perm} />. That way you can connect a server while keeping
            tools that change data behind a prompt. {helpLink("mcp-tool-permissions", "More in Help →")}
          </span>
        </div>

        <p className={styles.support}>
          Questions or problems with this server? Ask in <ExternalLink href={MCP_SLACK_URL}>{MCP_SLACK_LABEL}</ExternalLink>{" "}
          or <ExternalLink href={MCP_BUG_URL}>report a bug →</ExternalLink>
        </p>
      </div>

      <aside className={styles.sidebar} aria-label={`Connect to ${server.title}`}>
        <CopyServerUrlButton url={server.url} />

        {myConnections && <RecentUse serverUrl={server.url} serverTitle={server.title} />}

        <HowTo title="How to connect in Claude Desktop">
          <ol className={styles.steps}>
            <li>In Claude Desktop, open <strong>Customize</strong> and choose the <strong>Connectors</strong> tab</li>
            <li>Search for <strong>{name}</strong></li>
            <li>
              Pick the one with the <strong>CAI logo</strong> and a <strong>Custom</strong> badge. Search may also show the
              vendor&rsquo;s own public connector with a similar name; that one connects to the vendor directly, not
              through the platform
            </li>
            <li>Click it, then <strong>Connect</strong></li>
            <li>In the browser, sign in if asked, then click <strong>Allow</strong> on &ldquo;Authorize Claude&rdquo;</li>
            <li>
              It now shows under <strong>Yours</strong> with <strong>✓ Connected</strong>. Open it to set its{" "}
              <strong>Tool permissions</strong> (<ToolPermissionChoices />, per tool or per group)
            </li>
          </ol>
          <p className={styles.howToNote}>
            That <strong>Allow</strong> grants Claude access as you. Connectors you already have are under{" "}
            <strong>Yours</strong>; the rest are under <strong>More you can add</strong>.
          </p>
          <p className={styles.howToNote}>
            Not listed? Connectors are published to the Cox Claude organization by an admin, and not every platform server is
            published yet. Ask in <ExternalLink href={MCP_SLACK_URL}>{MCP_SLACK_LABEL}</ExternalLink>, or add it to Claude
            Code yourself.
          </p>
        </HowTo>

        <HowTo title="How to use it in Cowork">
          <p className={styles.howToText}>
            Nothing extra to do. Cowork runs inside Claude Desktop and uses the same connectors. In a new chat, pick{" "}
            <strong>Cowork</strong> with the <strong>Chat / Cowork</strong> toggle in the composer, and any connector
            you&rsquo;ve connected is available.
          </p>
        </HowTo>

        <HowTo title="How to connect in Claude Code">
          <p className={styles.howToHeading}>Reuse your Claude connector</p>
          <ol className={styles.steps}>
            <li>Run <code>claude</code>, then type <code>/mcp</code></li>
            <li>In the <strong>claude.ai</strong> section, expand <strong>Show unused connectors</strong></li>
            <li>
              Select <strong>{name}</strong>. It shows <code>needs authentication</code> until you sign in, then{" "}
              <code>connected · N tools</code>
            </li>
          </ol>
          <p className={styles.howToHeading}>Or configure it directly</p>
          <p className={styles.howToText}>
            Use the command under <strong>CLI connect commands</strong>. Choose this if you don&rsquo;t use Claude Desktop,
            want the setup explicit and reproducible, or want it committed so everyone on the repo gets it.
          </p>
        </HowTo>

        <details className={styles.cli}>
          <summary className={styles.cliSummary}>CLI connect commands</summary>
          <CopyRow label="Claude Code" text={cliCommand} />
          <CopyRow label="Project .mcp.json entry" text={mcpJsonEntry} />
          <CopyRow label="Server URL" text={server.url} />
          <HowTo title="How to connect via CLI">
            <p className={styles.howToHeading}>Claude Code</p>
            <p className={styles.howToText}>
              Run the command above from any terminal. Add <code>--scope</code> to choose where it lands:
            </p>
            <ClaudeCodeScopeTable />
            <p className={styles.howToText}>
              <code>claude mcp list</code> shows what&rsquo;s configured; <code>/mcp</code> in a session shows what&rsquo;s
              actually connected (<code>connected · N tools</code> vs <code>needs authentication</code>).
            </p>
            <p className={styles.howToHeading}>Other MCP clients</p>
            <ol className={styles.steps}>
              <li>Add a new <strong>HTTP</strong> (streamable HTTP) server</li>
              <li>Paste the server URL above</li>
            </ol>
          </HowTo>
        </details>

        <p className={styles.fullGuide}>{helpLink(undefined, "Full setup guide →")}</p>
      </aside>
    </div>
  );
}

/** One MCP server's page (#939, Feature F): what it is and how to connect to it. */
export default function McpServerDetailPage({
  serverName,
  onBackToServers,
  onHelp,
  myConnections = false,
  onOpenConnections,
}: Props) {
  const servers = useMcpServers();
  const server =
    servers.status === "ready"
      ? servers.result.servers.find((s) => s.serverName.toLowerCase() === serverName.toLowerCase())
      : undefined;

  return (
    <div>
      <Breadcrumb current={server?.title ?? serverName} onBack={onBackToServers} />

      {servers.status === "loading" && <p role="status" className={styles.status}>Loading server…</p>}

      {servers.status === "error" && (
        <div role="alert" className={styles.stateBlock}>
          <p className={styles.errorMessage}>Couldn&rsquo;t load the MCP server list.</p>
          <button type="button" onClick={servers.retry} className={styles.primaryButton}>Try again</button>
        </div>
      )}

      {servers.status === "ready" && !server && servers.result.source === "cached" && (
        <div role="alert" className={styles.stateBlock}>
          <h1 className={styles.title}>Couldn&rsquo;t reach the MCP registry</h1>
          <p className={styles.notFoundText}>
            <code>{serverName}</code> isn&rsquo;t in the list saved from{" "}
            {formatAddedDate(servers.result.fetchedAt) || "your last visit"}. It may have been added since.
          </p>
          <button type="button" onClick={servers.retry} className={styles.primaryButton}>Try again</button>
        </div>
      )}

      {servers.status === "ready" && !server && servers.result.source === "live" && (
        <div className={styles.stateBlock}>
          <h1 className={styles.title}>Server not found</h1>
          <p className={styles.notFoundText}>
            There&rsquo;s no MCP server named <code>{serverName}</code> on the platform. It may have been renamed or removed.
          </p>
          <a href={routeToHash({ page: "mcp" })} onClick={onSpaLinkClick(onBackToServers)} className={styles.link}>
            &larr; Back to MCP Servers
          </a>
        </div>
      )}

      {server && servers.status === "ready" && servers.result.source === "cached" && (
        <p className={styles.cachedNote}>
          The registry couldn&rsquo;t be reached, so these details are from{" "}
          {formatAddedDate(servers.result.fetchedAt) || "your last visit"} and may be out of date.
        </p>
      )}

      {server && (
        <ServerDetail server={server} onHelp={onHelp} myConnections={myConnections} onOpenConnections={onOpenConnections} />
      )}
    </div>
  );
}
